import React from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { InlineVisual, type InlineVisualSource } from "./source-pane/InlineVisual";
import rehypeFootnoteSeparators from "./footnoteSeparators";

// `[Quelle N]` oder kombinierte Formen wie `[Quelle 1, Quelle 2, 3]`
// als Inline-Citation. Das LLM schreibt diese Markierungen — wir machen
// daraus klickbare hochgestellte Zahlen.
// Match: '[' + (Quelle|Source|Ref|Chunk + N), kommagetrennt wiederholbar, + ']'
const CITE_BLOCK_RE =
  /\[\s*(?:Quelle|Quellen|Source|Sources|Ref|Chunk)?\s*\d+(?:\s*,\s*(?:Quelle|Quellen|Source|Sources|Ref|Chunk)?\s*\d+)*\s*\]/gi;
const NUM_RE = /\d+/g;

// Ende-Block "Genutzte Quellen:", "Quellen:", "Sources:" mit Liste
// drunter strippen — wir zeigen die Quellen im Collapsible-Pane unten.
const SOURCES_BLOCK_RE =
  /\n+(?:\*\*)?(?:Genutzte\s+)?(?:Quellen?|Sources?|Referenzen)(?:\*\*)?:?[\s\S]*$/i;


function stripSourcesBlock(content: string): string {
  return content.replace(SOURCES_BLOCK_RE, "").trim();
}


/** Sammelt alle in einem Text vorkommenden `[Quelle N]`-Nummern.
 * Wird genutzt um nach dem Absatz die zitierten Bilder/Tabellen
 * inline einzublenden. */
function collectCitedNumbers(text: string): number[] {
  const seen = new Set<number>();
  const re = new RegExp(CITE_BLOCK_RE.source, "gi");
  let m: RegExpExecArray | null;
  while ((m = re.exec(text)) !== null) {
    const numRe = /\d+/g;
    let nm: RegExpExecArray | null;
    while ((nm = numRe.exec(m[0])) !== null) {
      seen.add(parseInt(nm[0], 10));
    }
  }
  return [...seen];
}


/** Rekursive Text-Extraktion aus React children — nur Strings werden
 * konkateniert. Wird auf die rohen Markdown-Absätze angewandt, in denen
 * die `[Quelle N]`-Marker noch als Plain-Text vorliegen. */
function stringifyChildren(children: React.ReactNode): string {
  if (children == null || typeof children === "boolean") return "";
  if (typeof children === "string") return children;
  if (typeof children === "number") return String(children);
  if (Array.isArray(children)) return children.map(stringifyChildren).join("");
  if (React.isValidElement(children)) {
    return stringifyChildren((children.props as { children?: React.ReactNode }).children);
  }
  return "";
}


/** Ersetzt rekursiv in children alle `[Quelle N]`-Vorkommen durch
 * <sup><button>N</button></sup>. Geht nur durch Strings. */
function injectCitations(
  children: React.ReactNode,
  onCite: (n: number) => void,
  keyPrefix = "",
): React.ReactNode {
  if (typeof children === "string") {
    return splitTextOnCitations(children, onCite, keyPrefix);
  }
  if (Array.isArray(children)) {
    return children.map((c, i) =>
      typeof c === "string"
        ? splitTextOnCitations(c, onCite, `${keyPrefix}-${i}`)
        : c
    );
  }
  return children;
}


/** True, wenn ein Absatz NUR ein Bild (plus Whitespace) enthält — dann rendern
 * wir die Kinder ohne umschließendes <p>, damit kein ungültiges
 * <p><figure>…</figure></p> entsteht (Chart-Bilder tragen eine <figcaption>). */
function isImageOnlyParagraph(node: unknown): boolean {
  const n = node as
    | { children?: Array<{ type: string; value?: string }> }
    | undefined;
  if (!n?.children) return false;
  let foundImg = false;
  for (const c of n.children) {
    if (c.type === "image") foundImg = true;
    else if (c.type === "text" && /^\s*$/.test(c.value ?? "")) {
      /* Whitespace ist OK */
    } else return false;
  }
  return foundImg;
}


function splitTextOnCitations(
  text: string,
  onCite: (n: number) => void,
  keyPrefix: string,
): React.ReactNode[] {
  const parts: React.ReactNode[] = [];
  let last = 0;
  let blockIdx = 0;
  let m: RegExpExecArray | null;
  const re = new RegExp(CITE_BLOCK_RE.source, "gi");
  while ((m = re.exec(text)) !== null) {
    if (m.index > last) parts.push(text.slice(last, m.index));
    // Alle Zahlen aus dem Citation-Block extrahieren
    const nums: number[] = [];
    let nm: RegExpExecArray | null;
    const nre = new RegExp(NUM_RE.source, "g");
    while ((nm = nre.exec(m[0])) !== null) {
      nums.push(parseInt(nm[0], 10));
    }
    parts.push(
      <sup
        key={`${keyPrefix}-c${blockIdx++}`}
        className="inline-flex items-baseline mx-0.5 gap-px"
      >
        {nums.map((n, i) => (
          <button
            key={i}
            type="button"
            onClick={() => onCite(n)}
            className="text-[10px] font-semibold text-brand-600 dark:text-brand-400 hover:text-brand-700 dark:hover:text-brand-300 hover:bg-brand-100/60 dark:hover:bg-brand-700/20 rounded-md px-1 leading-tight transition-colors"
            title={`Quelle ${n} anzeigen`}
          >
            {n}
          </button>
        ))}
      </sup>
    );
    last = m.index + m[0].length;
  }
  if (last < text.length) parts.push(text.slice(last));
  return parts;
}


/** Light-weight Markdown-Renderer mit eigenen Tailwind-Klassen
 * (kein @tailwindcss/typography nötig). Wenn `onCite` gegeben ist,
 * werden `[Quelle N]`-Marker inline durch klickbare Superscript-
 * Buttons ersetzt und der "Genutzte Quellen:"-Block am Ende
 * automatisch entfernt — der gehört in das Collapsible-Pane, nicht
 * in den Fließtext. */
export default function Md({
  children,
  onCite,
  visualSources,
  onImageClick,
  isImageClickable,
}: {
  children: string;
  onCite?: (n: number) => void;
  /** Map Quellen-Nummer → Figure/Table-Source. Wenn ein Absatz
   * `[Quelle N]` zitiert und N hier liegt, wird das visuelle Material
   * direkt nach dem Absatz inline gerendert. */
  visualSources?: Map<number, InlineVisualSource>;
  /** Optional: macht die im Markdown gerenderten `<img>` klickbar (öffnet
   * z.B. das Bild-Edit-Panel). Ohne diesen Prop bleibt das Standard-
   * Verhalten (nicht-klickbare Bilder) — für alle anderen Md-Nutzer. */
  onImageClick?: (url: string) => void;
  /** Entscheidet je URL, ob das Bild klickbar ist (z.B. Dataviz-Charts NICHT).
   * Ohne Angabe sind alle Bilder klickbar, sofern `onImageClick` gesetzt ist. */
  isImageClickable?: (url: string) => boolean;
}) {
  const content = onCite ? stripSourcesBlock(children) : children;
  const wrap = (children: React.ReactNode, keyPrefix: string) =>
    onCite ? injectCitations(children, onCite, keyPrefix) : children;
  return (
    <div className="text-[15px] leading-relaxed">
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        rehypePlugins={[rehypeFootnoteSeparators]}
        components={{
          p: ({ node, children, ...props }) => {
            // Reines Bild-Absatz (z.B. ein Chart mit <figcaption>) → ohne <p>
            // rendern, sonst invalides <p><figure>-Markup.
            if (isImageOnlyParagraph(node)) return <>{children}</>;
            const visuals: InlineVisualSource[] = [];
            if (visualSources) {
              const text = stringifyChildren(children);
              for (const n of collectCitedNumbers(text)) {
                const v = visualSources.get(n);
                if (v) visuals.push(v);
              }
            }
            return (
              <>
                <p className="my-2 first:mt-0 last:mb-0" {...props}>
                  {wrap(children, "p")}
                </p>
                {visuals.length > 0 && (
                  <div className="my-2">
                    {visuals.map((v) => (
                      <InlineVisual
                        key={`${v.kind}-${v.n}`}
                        source={v}
                        onClick={onCite ? () => onCite(v.n) : undefined}
                      />
                    ))}
                  </div>
                )}
              </>
            );
          },
          img: ({ node: _node, src, alt, title, ...props }) => {
            const url = typeof src === "string" ? src : "";
            const clickable =
              !!onImageClick &&
              !!url &&
              (isImageClickable ? isImageClickable(url) : true);
            const img = (
              <img
                src={src}
                alt={alt ?? ""}
                {...props}
                onClick={clickable ? () => onImageClick!(url) : undefined}
                className={
                  clickable
                    ? "cursor-zoom-in transition hover:brightness-95 hover:ring-2 hover:ring-brand-400/50"
                    : undefined
                }
              />
            );
            // Chart-Provenienz (Markdown-Image-Title) → echte HTML-<figcaption>
            // unter dem Bild: lesbar, selektierbar, SEO-sichtbar (statt in die
            // Chart-Canvas gebacken und dort unlesbar herunterskaliert).
            const caption = typeof title === "string" ? title.trim() : "";
            if (!caption) return img;
            return (
              <figure className="my-4 [&>img]:!my-0">
                {img}
                <figcaption className="mt-2 text-xs leading-snug text-text-3">
                  {caption}
                </figcaption>
              </figure>
            );
          },
          strong: ({ node, ...props }) => (
            <strong className="font-semibold" {...props} />
          ),
          em: ({ node, ...props }) => <em className="italic" {...props} />,
          h1: ({ node, ...props }) => (
            <h1 className="text-lg font-bold mt-4 mb-2 first:mt-0" {...props} />
          ),
          h2: ({ node, ...props }) => (
            <h2 className="text-base font-bold mt-4 mb-2 first:mt-0" {...props} />
          ),
          h3: ({ node, ...props }) => (
            <h3 className="text-sm font-semibold mt-3 mb-1 first:mt-0" {...props} />
          ),
          ul: ({ node, ...props }) => (
            <ul className="list-disc pl-5 my-2 space-y-0.5" {...props} />
          ),
          ol: ({ node, ...props }) => (
            <ol className="list-decimal pl-5 my-2 space-y-0.5" {...props} />
          ),
          li: ({ node, children, ...props }) => (
            <li className="leading-snug" {...props}>
              {wrap(children, "li")}
            </li>
          ),
          code: ({ node, ...props }) => (
            <code
              className="text-[13px] bg-cream-100 dark:bg-ink-700 px-1 py-0.5 rounded-md"
              {...props}
            />
          ),
          pre: ({ node, ...props }) => (
            <pre
              className="bg-cream-100 dark:bg-ink-700 p-2 rounded-md overflow-x-auto my-2 text-[13px]"
              {...props}
            />
          ),
          a: ({ node, ...props }) => (
            <a
              className="text-brand-600 hover:underline"
              target="_blank"
              rel="noopener noreferrer"
              {...props}
            />
          ),
          blockquote: ({ node, ...props }) => (
            <blockquote
              className="border-l-2 border-cream-300 dark:border-ink-600 pl-3 my-2 text-ink-50/70 italic"
              {...props}
            />
          ),
          hr: () => <hr className="my-3 border-cream-200 dark:border-ink-600" />,
          table: ({ node, ...props }) => (
            <table className="w-full text-[13px] my-2 border-collapse" {...props} />
          ),
          th: ({ node, ...props }) => (
            <th
              className="text-left px-2 py-1 border border-cream-300 dark:border-ink-600 bg-cream-100 dark:bg-ink-700 font-medium"
              {...props}
            />
          ),
          td: ({ node, ...props }) => (
            <td
              className="px-2 py-1 border border-cream-300 dark:border-ink-600 align-top"
              {...props}
            />
          ),
        }}
      >
        {content}
      </ReactMarkdown>
    </div>
  );
}
